const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value))

const smoothstep = (start, end, value) => {
    const progress = clamp((value - start) / (end - start))
    return progress * progress * (3 - 2 * progress)
}

const seededRandom = (seed) => {
    const value = Math.sin(seed * 91.731 + 17.17) * 43758.5453
    return value - Math.floor(value)
}

const heartPoint = (angle) => {
    const x = 16 * Math.sin(angle) ** 3
    const y = 13 * Math.cos(angle) - 5 * Math.cos(2 * angle) - 2 * Math.cos(3 * angle) - Math.cos(4 * angle)
    return { x: x / 17, y: -y / 17 }
}

export function initForeverParticles() {
    const section = document.getElementById('page-forever')
    if (!section || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const canvas = document.createElement('canvas')
    canvas.className = 'forever__particles'
    canvas.setAttribute('aria-hidden', 'true')
    section.prepend(canvas)

    const context = canvas.getContext('2d', { alpha: true })
    let particles = []
    let width = 0
    let height = 0
    let pixelRatio = 1
    let progress = 0
    let lastTime = performance.now()
    let isVisible = false
    const pointer = { x: 0, y: 0, active: 0 }

    const buildParticles = () => {
        const rect = section.getBoundingClientRect()
        width = Math.max(1, rect.width)
        height = Math.max(1, rect.height)
        pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5)

        canvas.width = Math.round(width * pixelRatio)
        canvas.height = Math.round(height * pixelRatio)
        canvas.style.width = `${width}px`
        canvas.style.height = `${height}px`
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)

        const isMobile = width < 768
        const count = isMobile ? 440 : Math.min(980, Math.round(width * 0.72))
        const centerX = width * 0.5
        const centerY = height * (isMobile ? 0.47 : 0.5)
        const heartScale = Math.min(width * (isMobile ? 0.45 : 0.34), height * 0.45)

        particles = Array.from({ length: count }, (_, index) => {
            const randomA = seededRandom(index + 1)
            const randomB = seededRandom(index + 101)
            const randomC = seededRandom(index + 211)
            const angle = (index / count) * Math.PI * 2 + (randomA - 0.5) * 0.035
            const point = heartPoint(angle)
            const layer = randomB < 0.76 ? 1 : 0.72 + randomC * 0.2
            const targetX = centerX + point.x * heartScale * layer
            const targetY = centerY + point.y * heartScale * layer
            const edge = index % 4
            const spreadX = randomA * width
            const spreadY = randomB * height
            const startX = edge === 0 ? -width * 0.12 : edge === 1 ? width * 1.12 : spreadX
            const startY = edge === 2 ? -height * 0.12 : edge === 3 ? height * 1.12 : spreadY

            return {
                targetX,
                targetY,
                startX,
                startY,
                angle,
                phase: randomC * Math.PI * 2,
                size: 0.65 + randomA * 1.75,
                depth: 0.55 + randomB * 0.65,
                delay: randomC * 0.2,
                color: randomA > 0.78 ? '#ffe4d8' : randomA > 0.42 ? '#ffb4d0' : '#ff79ad'
            }
        })
    }

    const drawSparkle = (x, y, size, alpha) => {
        context.globalAlpha = alpha
        context.beginPath()
        context.moveTo(x - size * 2.4, y)
        context.lineTo(x + size * 2.4, y)
        context.moveTo(x, y - size * 2.4)
        context.lineTo(x, y + size * 2.4)
        context.stroke()
    }

    const render = (time) => {
        const delta = Math.min((time - lastTime) / 1000, 0.05)
        lastTime = time

        if (isVisible) {
            const rect = section.getBoundingClientRect()
            const targetProgress = clamp((window.innerHeight - rect.top) / (window.innerHeight * 0.9))
            progress += (targetProgress - progress) * (1 - Math.exp(-delta * 7))
            pointer.active += (0 - pointer.active) * (1 - Math.exp(-delta * 2.4))

            const gather = smoothstep(0.02, 0.88, progress)
            const settle = 1 - Math.pow(1 - gather, 3)
            const alpha = smoothstep(0, 0.18, progress)
            const orbitStrength = Math.sin(gather * Math.PI) * Math.min(width, height) * 0.14
            const breath = 1 + Math.sin(time * 0.0015) * 0.012 * settle

            context.clearRect(0, 0, width, height)
            context.globalCompositeOperation = 'lighter'
            context.lineCap = 'round'

            particles.forEach((particle, index) => {
                const localGather = smoothstep(particle.delay, 1, gather)
                const eased = 1 - Math.pow(1 - localGather, 3)
                const orbit = particle.angle + (1 - eased) * Math.PI * 1.65 + time * 0.00012
                const curveX = Math.cos(orbit + particle.phase) * orbitStrength * (1 - eased)
                const curveY = Math.sin(orbit + particle.phase) * orbitStrength * (1 - eased)
                let x = particle.startX + (particle.targetX - particle.startX) * eased + curveX
                let y = particle.startY + (particle.targetY - particle.startY) * eased + curveY

                if (settle > 0.72) {
                    const centerX = width * 0.5
                    const centerY = height * (width < 768 ? 0.47 : 0.5)
                    x = centerX + (x - centerX) * breath
                    y = centerY + (y - centerY) * breath
                    x += Math.cos(time * 0.0009 + particle.phase) * particle.depth * 1.4
                    y += Math.sin(time * 0.0011 + particle.phase) * particle.depth * 1.1
                }

                const dx = x - pointer.x
                const dy = y - pointer.y
                const distance = Math.hypot(dx, dy)
                if (pointer.active > 0.01 && distance < 120) {
                    const push = (1 - distance / 120) * 22 * pointer.active
                    x += (dx / Math.max(distance, 1)) * push
                    y += (dy / Math.max(distance, 1)) * push
                }

                const twinkle = 0.7 + Math.sin(time * 0.0024 + particle.phase) * 0.3
                const particleAlpha = alpha * particle.depth * (0.48 + settle * 0.38) * twinkle
                const size = particle.size * (0.72 + eased * 0.42)

                context.fillStyle = particle.color
                context.globalAlpha = particleAlpha
                context.beginPath()
                context.arc(x, y, size, 0, Math.PI * 2)
                context.fill()

                if (index % 79 === 0 && settle > 0.65 && twinkle > 0.9) {
                    context.strokeStyle = particle.color
                    drawSparkle(x, y, size, particleAlpha * 0.7)
                }
            })

            context.globalAlpha = 1
            context.globalCompositeOperation = 'source-over'
        }

        requestAnimationFrame(render)
    }

    const visibilityObserver = new IntersectionObserver(([entry]) => {
        isVisible = entry.isIntersecting
        if (!isVisible) context.clearRect(0, 0, width, height)
    }, { root: document.getElementById('fake-scroll'), rootMargin: '35% 0px' })

    const resizeObserver = new ResizeObserver(buildParticles)
    const handlePointerMove = (event) => {
        const rect = section.getBoundingClientRect()
        pointer.x = event.clientX - rect.left
        pointer.y = event.clientY - rect.top
        pointer.active = 1
    }
    const handlePointerLeave = () => { pointer.active = 0 }

    visibilityObserver.observe(section)
    resizeObserver.observe(section)
    section.addEventListener('pointermove', handlePointerMove, { passive: true })
    section.addEventListener('pointerleave', handlePointerLeave, { passive: true })
    buildParticles()
    requestAnimationFrame(render)
}
